import React, { useState, useEffect, useRef } from 'react'
import './ProductDetailPage.css'
import httpClient from '../components/helpers/httpClient'
import { useNavigate, useParams } from "react-router-dom"
import MenuBar from "../components/MenuBar"
import Header from "../components/Header"
import LoadingSpinner from "../components/placeholders/LoadingSpinner"
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import Dropdown from '../components/Dropdown'
import { toast } from "react-toastify"
import FileDropper from '../components/FileDropper'
import CloseIcon from '@mui/icons-material/Close'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PopupDropdown from '../components/PopupDropdown'
import DropdownMultiselect from '../components/DropdownMultiselect'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckBox from '../components/CheckBox'
import { theme } from 'antd'
import { FaCalculator, FaEdit, FaTrash } from "react-icons/fa"
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { formatDate } from '../components/helpers/formatDate'
import DocumentsSection from '../components/DocumentsSection'
import Button from '../components/UI/Button'

const ImageViewer = ({ images, selectedIndex, onClose, onNavigate }) => {
  const hasMultipleImages = images.length > 1
  const selectedImage = images[selectedIndex]
  const imageUrl = selectedImage?.file_url

  if (!imageUrl) return null

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer-content" onClick={(e) => e.stopPropagation()}>
        {hasMultipleImages && (
          <button
            type="button"
            className="image-viewer-nav image-viewer-nav--previous"
            onClick={() => onNavigate(-1)}
            aria-label="Previous image"
          >
            <ChevronLeftIcon />
          </button>
        )}
        <div className="image-viewer-frame">
          <img src={imageUrl} alt="Full size" />
        </div>
        {hasMultipleImages && (
          <button
            type="button"
            className="image-viewer-nav image-viewer-nav--next"
            onClick={() => onNavigate(1)}
            aria-label="Next image"
          >
            <ChevronRightIcon />
          </button>
        )}
        <button className="close-button" onClick={onClose}>×</button>
      </div>
    </div>
  )
}

const PriceEditWarningPopup = ({ isOpen, onConfirm, onCancel, affectedPOCount }) => {
  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-content warning-popup">
        <h3>⚠️ Price Edit Warning</h3>
        <div className="warning-message">
          <p>This price line is currently linked to purchase order lines.</p>
          <p>Editing this price will:</p>
          <ul>
            <li>Create a new price line with your changes</li>
            <li>Keep existing PO lines with status "sent" or "accepted" unchanged</li>
            <li>Update other PO lines to use the new price</li>
          </ul>
          {affectedPOCount > 0 && (
            <p className="affected-po-info">
              <strong>{affectedPOCount}</strong> PO line(s) with status "sent" or "accepted" will remain unchanged.
            </p>
          )}
        </div>
        <div className="popup-buttons">
          <button onClick={onCancel} className="primary-button red">
            Cancel
          </button>
          <button onClick={onConfirm} className="primary-button">
            Continue with Edit
          </button>
        </div>
      </div>
    </div>
  );
};

function ProductDetailPage() {
  const defaultPriceState = {
    supplier_id: null,
    installer_id: null,
    product_id_supplier: '',
    SKU_supplier: '',
    unit: 'Stuk',
    price_net_excl_installation: '',
    price_brut_excl_installation: '',
    margin_excl_installation: '',
    installation_percentage_on_net: '',
    installation_percentage_on_brut: '',
    installation_fixed_price: '',
    price_net_installation: '',
    price_brut_installation: '',
    margin_installation: '',
    price_brut_incl_installation: '',
    installation_type: 'percentage_on_net',
    manually_edited_brut_incl: false
  }

  const [product, setProduct] = useState({})
  const [edit, setEdit] = useState(false)
  const [fileURL, setFileURL] = useState("")
  const [images, setImages] = useState([])
  const [files, setFiles] = useState([])
  const [prices, setPrices] = useState([])
  const [companies, setCompanies] = useState([])
  const [selectedImageIndex, setSelectedImageIndex] = useState(null)
  const [newPrice, setNewPrice] = useState({ ...defaultPriceState })
  const [editingPriceId, setEditingPriceId] = useState(null)
  const [showPriceForm, setShowPriceForm] = useState(false)
  const [groups, setGroups] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [expandedSection, setExpandedSection] = useState(null)
  const [showPriceWarning, setShowPriceWarning] = useState(false)
  const [priceEditWarningData, setPriceEditWarningData] = useState({})
  const [originalTotalPrice, setOriginalTotalPrice] = useState('')
  const [expandedUsageCells, setExpandedUsageCells] = useState({})
  const unitOptions = ['Stuk', 'm²', 'm³', 'lm', 'kg', 'l'] // Available unit options

  const navigate = useNavigate()
  const { productID } = useParams()

  const fetchProduct = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/products/get?productID=${productID}`)

    const data = response.data

    setImages(data.product_images || [])
    setFiles(data.product_files || [])
    setFileURL(data.technical_fiche_link)
    setPrices(data.product_prices || [])
    setSelectedGroups(data.product_groups || [])
    setProduct(response.data)
  }

  const fetchCompanies = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/companies/get`)
    // Map companies to only include id and name
    const mappedCompanies = response.data.map(company => ({
      id: company.id,
      name: company.company_name
    }))
    setCompanies(mappedCompanies)
  }

  const fetchGroups = async () => {
    const response = await httpClient.get(import.meta.env.VITE_API_URL + '/api/product/groups/get')
    setGroups(response.data)
  }

  const toggleUsageCell = (cellKey) => {
    setExpandedUsageCells(prev => ({
      ...prev,
      [cellKey]: !prev[cellKey]
    }))
  }

  const renderUsageLabels = (items, cellKey) => {
    if (!Array.isArray(items) || items.length === 0) {
      return <span className="price-usage-empty">-</span>
    }

    const isExpanded = !!expandedUsageCells[cellKey]
    const visibleItems = isExpanded ? items : items.slice(0, 3)
    const hiddenCount = items.length - visibleItems.length

    return (
      <div className="price-usage-labels">
        {visibleItems.map((item, index) => (
          <span key={item.id || `${item.name}-${index}`} className="price-usage-label">
            {item.name || '-'}
          </span>
        ))}
        {items.length > 3 && (
          <button
            type="button"
            className="price-usage-more"
            onClick={() => toggleUsageCell(cellKey)}
            title={isExpanded ? 'Show fewer' : `Show ${hiddenCount} more`}
          >
            {isExpanded ? 'Show less' : `+${hiddenCount} more`}
          </button>
        )}
      </div>
    )
  }

  const handleImageViewerNavigate = (direction) => {
    setSelectedImageIndex((currentIndex) => {
      if (currentIndex === null || images.length === 0) return null

      return (currentIndex + direction + images.length) % images.length
    })
  }

  useEffect(() => {
    fetchProduct()
    fetchCompanies()
    fetchGroups()
  }, [])

  // Keep fileURL synced with product.technical_fiche_link
  useEffect(() => {
    if (product.technical_fiche_link !== undefined) {
      setFileURL(product.technical_fiche_link)
    }
  }, [product.technical_fiche_link])

  // Update product.technical_fiche_link when fileURL changes
  useEffect(() => {
    if (fileURL !== undefined && product.technical_fiche_link !== fileURL) {
      setProduct(prev => ({
        ...prev,
        technical_fiche_link: fileURL
      }))
    }
  }, [fileURL])

  useEffect(() => {
    // Re-calculate installation prices whenever relevant values change
    if (newPrice.price_brut_excl_installation && 
        newPrice.installation_type === 'percentage_on_brut' && 
        newPrice.installation_percentage_on_brut) {
      const updates = updateInstallationPrices({...newPrice});
      setNewPrice(updates);
    }
  }, [newPrice.price_brut_excl_installation, newPrice.installation_percentage_on_brut]);

  // Helper function to update installation prices based on type - moved here to avoid hoisting issues
  const updateInstallationPrices = (updates) => {
    // Skip if we don't have the base prices needed for calculations
    if (updates.installation_type === 'percentage_on_net' && (!updates.price_net_excl_installation || !updates.installation_percentage_on_net)) return updates;
    if (updates.installation_type === 'percentage_on_brut' && (!updates.price_brut_excl_installation || !updates.installation_percentage_on_brut)) return updates;
    if (updates.installation_type === 'fixed_price' && !updates.installation_fixed_price) return updates;

    // Skip updating price_brut_incl_installation if it was manually edited
    const skipBrutUpdate = updates.manually_edited_brut_incl;

    if (updates.installation_type === 'percentage_on_net' && updates.installation_percentage_on_net) {
      // Calculate installation cost based on percentage of net price
      const netPrice = parseFloat(updates.price_net_excl_installation);
      const installationPercentage = parseFloat(updates.installation_percentage_on_net);
      const installationAmount = netPrice * installationPercentage / 100;

      // Set price_net_installation to just the installation cost 
      updates.price_net_installation = installationAmount.toFixed(2);
      
      // Clear brut installation and margin to ensure they're calculated manually
      updates.price_brut_installation = '';
      updates.margin_installation = '';

      // Calculate total price if brut installation is provided manually
      if (!skipBrutUpdate && updates.price_brut_excl_installation && updates.price_brut_installation) {
        updates.price_brut_incl_installation = (
          parseFloat(updates.price_brut_excl_installation) + 
          parseFloat(updates.price_brut_installation)
        ).toFixed(2);
      }
    } else if (updates.installation_type === 'percentage_on_brut' && updates.installation_percentage_on_brut) {
      // Calculate installation cost based on percentage of brut price
      const brutPrice = parseFloat(updates.price_brut_excl_installation);
      const installationPercentage = parseFloat(updates.installation_percentage_on_brut);
      const installationAmount = brutPrice * installationPercentage / 100;

      // For percentage on brut, the brut installation is directly calculated from brut product price
      updates.price_brut_installation = installationAmount.toFixed(2);
      
      // Calculate the net_installation as a direct percentage of brut price, similar to how % on net works
      updates.price_net_installation = installationAmount.toFixed(2);
      
      // Clear margin to ensure it's calculated manually
      updates.margin_installation = '';

      // Calculate total price
      if (!skipBrutUpdate) {
        updates.price_brut_incl_installation = (
          parseFloat(updates.price_brut_excl_installation) + 
          parseFloat(updates.price_brut_installation)
        ).toFixed(2);
      }
    } else if (updates.installation_type === 'fixed_price' && updates.installation_fixed_price) {
      const fixedPrice = parseFloat(updates.installation_fixed_price);

      // For fixed price, the net installation is the fixed amount
      updates.price_net_installation = fixedPrice.toFixed(2);
      
      // Clear brut installation and margin to ensure they're calculated manually
      updates.price_brut_installation = '';
      updates.margin_installation = '';

      // Calculate total price if brut installation is provided
      if (!skipBrutUpdate && updates.price_brut_excl_installation && updates.price_brut_installation) {
        updates.price_brut_incl_installation = (
          parseFloat(updates.price_brut_excl_installation) + 
          parseFloat(updates.price_brut_installation)
        ).toFixed(2);
      }
    }

    return updates;
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleEditProduct = async () => {
    if (edit) {
      const editButton = document.querySelector("#product__btn-edit")
      editButton.disabled = true

      // Add price
      await AddPrice()

      // Update product
      try {
        const response = await toast.promise(
          httpClient.put(import.meta.env.VITE_API_URL + `/api/product/update?productID=${productID}`, product),
          {
            pending: "Update product...",
            success: "Product has been updated 👍",
            error: "Something went wrong 😢"
          }
        )

        fetchProduct()

        editButton.disabled = false
        handleCancelEdit()
        setEdit(!edit)
      } catch (error) {
        console.error("Error updating product:", error)
        editButton.disabled = false
      }
    } else {
      handleCancelEdit()
      setEdit(true)
    }
  }

  const handleChangeInput = (e, type) => {
    // Skip any attempts to change product_internal_sku
    if (type === "product_internal_sku") {
      return;
    }

    const newProductObj = { ...product }
    newProductObj[type] = e.currentTarget.value

    setProduct(newProductObj)
  }

  const handleUploadFiles = async (e, type) => {
    if (e.target.files && e.target.files.length > 0) {
      const formData = new FormData()

      // Append all files
      Array.from(e.target.files).forEach(file => {
        formData.append('files[]', file)
      })
      formData.append('product_id', productID)

      try {
        const response = await toast.promise(
          httpClient.post(
            import.meta.env.VITE_API_URL + '/api/products/upload-files?type=' + type,
            formData
          ),
          {
            pending: 'Uploading images...',
            success: 'Images uploaded successfully',
            error: 'Failed to upload images'
          }
        )

        // Add all new image URLs to the existing images
        const newUrls = response.data.urls.map(item => item.url)
        if (type == "image") {
          setImages([...images, ...newUrls])
        } else {
          setFiles([...files, ...newUrls])
        }
      } catch (error) {
        console.error('Upload error:', error)
      }
    }

    fetchProduct()
  }

  const handleDeleteFile = async (fileId) => {
    try {
      await toast.promise(
        httpClient.delete(
          `${import.meta.env.VITE_API_URL}/api/products/delete-file?fileID=${fileId}`
        ),
        {
          pending: 'Deleting file...',
          success: 'File deleted successfully',
          error: 'Failed to delete file'
        }
      )

      // Refresh product data after deletion
      fetchProduct()
    } catch (error) {
      console.error('Delete error:', error)
    }
  }

  const handlePriceChange = (field, value) => {
    const updates = { ...newPrice, [field]: value }
    
    // When changing installation type, clear other installation values
    if (field === 'installation_type') {
      // Clear all installation related values first
      updates.installation_percentage_on_net = '';
      updates.installation_percentage_on_brut = '';
      updates.installation_fixed_price = '';
      updates.price_net_installation = '';
      updates.price_brut_installation = '';
      updates.margin_installation = '';
      
      // Preserve the total price if it was manually edited
      if (!updates.manually_edited_brut_incl) {
        updates.price_brut_incl_installation = '';
      }
    }
    
    // Track if brut price including installation is manually edited
    if (field === 'price_brut_incl_installation') {
      updates.manually_edited_brut_incl = true;
    } else if (field === 'price_brut_installation' || field === 'margin_installation') {
      // Update the price_brut_incl_installation if not manually edited
      // but only if we have both product price and installation price
      if (!updates.manually_edited_brut_incl && 
          updates.price_brut_excl_installation && 
          updates.price_brut_installation) {
        updates.price_brut_incl_installation = (
          parseFloat(updates.price_brut_excl_installation) + 
          parseFloat(updates.price_brut_installation)
        ).toFixed(2);
      }
    }

    // Only keep installation calculations
    if (!updates.manually_edited_brut_incl && (
        field === 'installation_percentage_on_net' || 
        field === 'installation_percentage_on_brut' || 
        field === 'installation_fixed_price' ||
        field === 'price_brut_excl_installation' ||
        (field === 'installation_type' && (
          updates.installation_percentage_on_net || 
          updates.installation_percentage_on_brut || 
          updates.installation_fixed_price
        ))
    )) {
      updateInstallationPrices(updates);
    }

    setNewPrice(updates)
  }

  // Smart function to calculate prices based on available fields
  const calculateSmartPrices = (netField, marginField, brutField, fieldType = 'excl') => {
    const updates = { ...newPrice };
    const net = parseFloat(updates[netField]) || 0;
    const margin = parseFloat(updates[marginField]) || 0;
    const brut = parseFloat(updates[brutField]) || 0;

    // Count how many fields have values
    const fieldsWithValues = [net, margin, brut].filter(val => val > 0).length;

    if (fieldsWithValues === 0) {
      toast.warning("Please enter at least one value to calculate");
      return;
    } else if (fieldsWithValues === 1) {
      toast.warning("Please enter at least two values to perform calculation");
      return;
    } else if (fieldsWithValues === 2) {
      // Calculate the missing field
      if (net > 0 && margin > 0 && brut === 0) {
        // Net + Margin => calculate Brut
        updates[brutField] = (net * (1 + margin / 100)).toFixed(2);
      } else if (brut > 0 && margin > 0 && net === 0) {
        // Brut + Margin => calculate Net
        updates[netField] = (brut / (1 + margin / 100)).toFixed(2);
      } else if (net > 0 && brut > 0 && margin === 0) {
        // Net + Brut => calculate Margin
        updates[marginField] = (((brut / net) - 1) * 100).toFixed(2);
      }
    } else if (fieldsWithValues === 3) {
      // All fields provided - recalculate Brut based on Net * Margin (most common scenario)
      const newBrut = (net * (1 + margin / 100)).toFixed(2);
      updates[brutField] = newBrut;
    }

    // After calculation, update installation prices if needed and this is for the main product prices
    if (fieldType === 'excl' && !updates.manually_edited_brut_incl) {
      // Instead of calling updateInstallationPrices which might clear fields,
      // let's preserve the installation calculation and recalculate based on the new base prices
      
      // If we have installation percentage on net, recalculate installation prices
      if (updates.installation_type === 'percentage_on_net' && updates.installation_percentage_on_net && updates.price_net_excl_installation) {
        const netPrice = parseFloat(updates.price_net_excl_installation);
        const installationPercentage = parseFloat(updates.installation_percentage_on_net);
        const installationAmount = netPrice * installationPercentage / 100;
        updates.price_net_installation = installationAmount.toFixed(2);
        
        // If we had a margin for installation, recalculate brut installation
        if (updates.margin_installation) {
          const marginInstall = parseFloat(updates.margin_installation);
          updates.price_brut_installation = (installationAmount * (1 + marginInstall / 100)).toFixed(2);
        }
      }
      
      // If we have installation percentage on brut, recalculate installation prices  
      else if (updates.installation_type === 'percentage_on_brut' && updates.installation_percentage_on_brut && updates.price_brut_excl_installation) {
        const brutPrice = parseFloat(updates.price_brut_excl_installation);
        const installationPercentage = parseFloat(updates.installation_percentage_on_brut);
        const installationAmount = brutPrice * installationPercentage / 100;
        updates.price_brut_installation = installationAmount.toFixed(2);
        updates.price_net_installation = installationAmount.toFixed(2);
      }
      
      // If we have fixed price installation, keep it as is (no recalculation needed)
      
      // Update total price if we have both product and installation prices
      if (updates.price_brut_excl_installation && updates.price_brut_installation) {
        updates.price_brut_incl_installation = (
          parseFloat(updates.price_brut_excl_installation) +
          parseFloat(updates.price_brut_installation)
        ).toFixed(2);
      }
    }

    setNewPrice(updates);
    return updates; // Return updates for use in other functions
  }

  // New function to calculate prices based on available fields - replaced the old calculatePrices
  const calculatePrices = () => {
    calculateSmartPrices(
      'price_net_excl_installation',
      'margin_excl_installation', 
      'price_brut_excl_installation',
      'excl'
    );
  }

  // New function for installation price calculation
  const calculateInstallationPrices = () => {
    const updatedState = calculateSmartPrices(
      'price_net_installation',
      'margin_installation',
      'price_brut_installation', 
      'installation'
    );
    
    // After installation calculation, update the total price if we have the product price
    if (updatedState && !updatedState.manually_edited_brut_incl && 
        updatedState.price_brut_excl_installation && 
        updatedState.price_brut_installation) {
      const finalUpdates = { ...updatedState };
      finalUpdates.price_brut_incl_installation = (
        parseFloat(finalUpdates.price_brut_excl_installation) +
        parseFloat(finalUpdates.price_brut_installation)
      ).toFixed(2);
      setNewPrice(finalUpdates);
    }
  }

  const handleEditPrice = (price) => {
    if (!edit) return; // Only allow editing when in edit mode

    // Determine which installation type is being used
    let installationType = 'percentage_on_net';
    if (price.installation_percentage_on_brut) {
      installationType = 'percentage_on_brut';
    } else if (price.installation_fixed_price) {
      installationType = 'fixed_price';
    }

    // Find supplier and installer objects for the dropdowns
    const supplier = companies.find(c => c.id === price.supplier_id);
    const installer = companies.find(c => c.id === price.installer_id);

    // Set the form with the price data
    setNewPrice({
      supplier_id: supplier,
      installer_id: installer,
      product_id_supplier: price.SKU_supplier || '',
      SKU_supplier: price.SKU_supplier || '',
      unit: price.unit || 'Stuk',
      price_net_excl_installation: price.price_net_excl_installation || '',
      price_brut_excl_installation: price.price_brut_excl_installation || '',
      margin_excl_installation: price.margin_excl_installation || '',
      installation_percentage_on_net: price.installation_percentage_on_net || '',
      installation_percentage_on_brut: price.installation_percentage_on_brut || '',
      installation_fixed_price: price.installation_fixed_price || '',
      price_net_installation: price.price_net_installation || '',
      price_brut_installation: price.price_brut_installation || '',
      margin_installation: price.margin_installation || '',
      price_brut_incl_installation: price.price_brut_incl_installation || '',
      installation_type: installationType,
      manually_edited_brut_incl: false
    });

    // Store the original total price for comparison
    setOriginalTotalPrice(price.price_brut_incl_installation || '');

    // Set the editing ID
    setEditingPriceId(price.id);
    setShowPriceForm(true);

    // Scroll to the price form
    document.getElementById('prices-section').scrollIntoView({ behavior: 'smooth' });
  }

  const handleCreateNewPrice = () => {
    setNewPrice({ ...defaultPriceState });
    setEditingPriceId(null);
    setOriginalTotalPrice('');
    setShowPriceWarning(false);
    setPriceEditWarningData({});
    setShowPriceForm(true);

    document.getElementById('prices-section').scrollIntoView({ behavior: 'smooth' });
  }

  const handleCancelEdit = () => {
    // Reset form and clear editing state
    setNewPrice({ ...defaultPriceState });
    setEditingPriceId(null);
    setOriginalTotalPrice('');
    setShowPriceWarning(false);
    setPriceEditWarningData({});
    setShowPriceForm(false);
  }

  const handleGroupsChange = async (group, action) => {
    try {
      if (action === "add") {
        await httpClient.post(import.meta.env.VITE_API_URL + '/api/product/groups/add-to-group', {
          product_id: productID,
          group_id: group.id
        })
        setSelectedGroups(prev => [...prev, group])
      } else {
        await httpClient.post(import.meta.env.VITE_API_URL + `/api/product/groups/remove-from-group`, {
          product_id: productID,
          group_id: group.id
        })
        setSelectedGroups(prev => prev.filter(g => g.id !== group.id))
      }
    } catch (error) {
      console.error('Error updating groups:', error)
      // revert the local state change on error
      fetchProduct()
    }
  }

  const toggleSectionExpand = (sectionId) => {
    if (expandedSection === sectionId) {
      setExpandedSection(null) // Collapse if already expanded
    } else {
      setExpandedSection(sectionId) // Expand the clicked section
    }
  }

  const handleDeletePrice = async (priceId) => {
    // Find the price object to check if it's updatable
    const price = prices.find(p => p.id === priceId);
    if (price && !price.updatable) {
      toast.warning("Cannot delete this price as it is already linked to a purchase order");
      return;
    }

    try {
      const response = await toast.promise(
        httpClient.delete(
          `${import.meta.env.VITE_API_URL}/api/products/delete-price?priceID=${priceId}`
        ),
        {
          pending: 'Deleting price...',
          success: 'Price deleted successfully',
          error: {
            render({ data }) {
              // When the promise is rejected, data will contain the error
              if (data && data.response && data.response.data && data.response.data.message) {
                return data.response.data.message;
              }
              return 'Failed to delete price';
            }
          }
        }
      );

      // Refresh prices after deletion
      fetchProduct();
    } catch (error) {
      console.error('Error deleting price:', error);
      // Already handled by toast
    }
  };

  const checkPriceEditImpact = async () => {
    try {
      if (!editingPriceId) return false;

      console.log("Checking price edit impact")
      console.log(editingPriceId)
      
      // Check if total price has changed
      const currentTotalPrice = newPrice.price_brut_incl_installation || '';
      if (currentTotalPrice === originalTotalPrice) {
        return false; // No price change, no need for warning
      }

      // Call backend to check for affected PO lines
      const response = await httpClient.get(
        `${import.meta.env.VITE_API_URL}/api/products/check-price-edit-impact/${editingPriceId}`
      );
      console.log(response.data)
      if (response.data.hasAffectedPOS) {
        setPriceEditWarningData({
          affectedPOCount: response.data.affectedPOCount,
          priceId: editingPriceId
        });
        setShowPriceWarning(true);
        return true; // Need to show warning
      }

      return false; // No affected PO lines
    } catch (error) {
      console.error('Error checking price edit impact:', error);
      return false;
    }
  };

  const confirmPriceEdit = async () => {
    setShowPriceWarning(false);
    await performPriceUpdate(true); // true indicates this is a confirmed edit with PO impact
  };

  const performPriceUpdate = async (isConfirmedEdit = false) => {
    try {
      if (!newPrice.supplier_id && !newPrice.installer_id) {
        return;
      }

      // Create a clean copy of the newPrice object without the manually_edited_brut_incl flag
      const priceToSend = { ...newPrice };
      delete priceToSend.manually_edited_brut_incl;

      // Ensure we're sending supplier_id and installer_id as IDs not objects
      if (priceToSend.supplier_id && typeof priceToSend.supplier_id === 'object') {
        priceToSend.supplier_id = priceToSend.supplier_id.id;
      }
      if (priceToSend.installer_id && typeof priceToSend.installer_id === 'object') {
        priceToSend.installer_id = priceToSend.installer_id.id;
      }

      // Add these for backward compatibility
      priceToSend.product_id = productID;

      let url, method;
      if (editingPriceId) {
        if (isConfirmedEdit) {
          // Use the sophisticated edit endpoint that creates new price line
          url = `${import.meta.env.VITE_API_URL}/api/products/sophisticated-price-edit/${editingPriceId}`;
          method = 'post';
        } else {
          // Regular update
          url = `${import.meta.env.VITE_API_URL}/api/products/update-price/${editingPriceId}`;
          method = 'post';
        }
      } else {
        // New price
        url = `${import.meta.env.VITE_API_URL}/api/products/add-price`;
        method = 'post';
      }

      const response = await toast.promise(
        httpClient[method](url, priceToSend),
        {
          pending: editingPriceId ? 'Updating price...' : 'Adding price...',
          success: editingPriceId ? 'Price updated successfully' : 'Price added successfully',
          error: {
            render({ data }) {
              // When the promise is rejected, data will contain the error
              if (data && data.response && data.response.data && data.response.data.message) {
                return data.response.data.message;
              }
              return editingPriceId ? 'Failed to update price' : 'Failed to add price';
            }
          }
        }
      );

      // Reset form after success
      handleCancelEdit();

      // Refresh product data with updated prices
      fetchProduct();
    } catch (error) {
      console.error('Error adding/updating price:', error);
      // Already handled by toast
    }
  };

  const AddPrice = async () => {
    // Check if we need to show warning for price edits
    const needsWarning = await checkPriceEditImpact();
    
    if (needsWarning) {
      // Warning popup will be shown, user needs to confirm
      return;
    }

    // No warning needed, proceed with update
    await performPriceUpdate();
  };

  return (
    <div className='template'>

      <Header>
        <div className="back_button" onClick={handleGoBack}><KeyboardBackspaceIcon /> Go Back</div>

        <div className='header__options--right'>
          <Button variant='primary' size='sm' id="product__btn-edit" onClick={handleEditProduct}>{edit ? "Save" : "Edit Details"}</Button>
          {edit && <Button variant='primary' tone='red' size='sm' id="product__btn-edit" onClick={() => {
            setEdit(false)
            handleCancelEdit()
          }}>Cancel</Button>}
        </div>
      </Header>

      <MenuBar />

      <div className="template__body" id='body-product-detail'>
        <div
          className={`body__section ${expandedSection ? 'has-expanded' : ''}`}
          id="product-detail"
        >
          {/* Top section with 3 columns */}
          <div
            className={`form__section ${expandedSection === 'product-info-section' ? 'expanded full-width-content' : ''}`}
            id='product-info-section'
          >
            <h3 onClick={() => toggleSectionExpand('product-info-section')}>Basic Info</h3>
            
            <div className="section-content">
              <div className="input_field">
                <label className="input_field__label" htmlFor="email">Brand</label>
                <input className="input_field__input_box--text" id="products__brand" type="text" name="brand" value={product.brand} onChange={(e) => handleChangeInput(e, "brand")} disabled={!edit} />
              </div>

              <div className="input_field">
                <label className="input_field__label" htmlFor="tel">Product</label>
                <input className="input_field__input_box--text" id="products__product" type="text" name="product" value={product.product} onChange={(e) => handleChangeInput(e, "product")} disabled={!edit} />
              </div>

              <div className="input_field">
                <label className="input_field__label">Groups</label>
                <DropdownMultiselect
                  listArray={groups}
                  state={selectedGroups}
                  setState={handleGroupsChange}
                  width="100%"
                  idList="groups-dropdown"
                  displayValueKey="group_name"
                  allowCreate={true}
                  createEndpoint="/api/product/groups/create"
                  createValueKey="group_name"
                  disabled={!edit}
                />
              </div>

              <div className="input_field">
                <label className="input_field__label" htmlFor="tel">Description</label>
                <textarea className="input_field__input_box--text" id="product__product_description" type="text" name="product_description" value={product.product_description} onChange={(e) => handleChangeInput(e, "product_description")} disabled={!edit} />
              </div>

              <div className="horizontal-fields">
                <div className="input_field">
                  <label className="input_field__label" htmlFor="tel">Product Code Producer</label>
                  <input className="input_field__input_box--text" id="products__product_producer_id" type="text" name="product_producer_id" value={product.product_producer_id} onChange={(e) => handleChangeInput(e, "product_producer_id")} disabled={!edit} />
                </div>

                <div className="input_field">
                  <label className="input_field__label" htmlFor="tel">Product Internal SKU</label>
                  <input className="input_field__input_box--text" id="products__product_internal_sku" type="text" name="product_internal_sku" value={product.product_internal_sku} onChange={(e) => handleChangeInput(e, "product_internal_sku")} disabled={true} />
                </div>
              </div>

              <div className="input_field is-active-field">
                <label className="input_field__label">Active Status</label>
                <CheckBox
                  state={product.is_active || false}
                  setState={(newState) => {
                    setProduct({
                      ...product,
                      is_active: newState
                    })
                  }}
                  textPosition="right"
                  disabled={!edit}
                  width="50%"
                  textSize="0.8rem"
                />
              </div>
            </div>
          </div>

          {/* Prices section */}
          <div
            className={`form__section ${expandedSection === 'prices-section' ? 'expanded' : ''}`}
            id="prices-section"
          >
            <h3 onClick={() => toggleSectionExpand('prices-section')}>Prices</h3>
            
            <div className="section-content">
              {/* Add new price form */}
              {edit && showPriceForm && (
                <div className="add-price-form">
                  <div className="price-form-grid">
                    {/* Row 1: Installer, Supplier, SKU, Unit */}
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Installer</label>
                        <PopupDropdown
                          options={companies}
                          value={newPrice.installer_id}
                          onChange={(installer) => handlePriceChange('installer_id', installer)}
                          title="Select Installer"
                          searchBar={true}
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Supplier</label>
                        <PopupDropdown
                          options={companies}
                          value={newPrice.supplier_id}
                          onChange={(supplier) => handlePriceChange('supplier_id', supplier)}
                          title="Select Supplier"
                          searchBar={true}
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">SKU Supplier</label>
                        <input
                          type="text"
                          placeholder="Enter SKU"
                          value={newPrice.SKU_supplier}
                          onChange={(e) => handlePriceChange('SKU_supplier', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Unit</label>
                        <select
                          value={newPrice.unit}
                          onChange={(e) => handlePriceChange('unit', e.target.value)}
                          className="input_field__input_box--text"
                        >
                          {unitOptions.map((unit, index) => (
                            <option key={index} value={unit}>{unit}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Row 2: Net excl, Margin excl, Gross excl */}
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Net Price excl. installation</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newPrice.price_net_excl_installation}
                          onChange={(e) => handlePriceChange('price_net_excl_installation', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Margin % excl. installation</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newPrice.margin_excl_installation}
                          onChange={(e) => handlePriceChange('margin_excl_installation', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Gross Price excl. installation</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newPrice.price_brut_excl_installation}
                          onChange={(e) => handlePriceChange('price_brut_excl_installation', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <button
                        className="calculator-button"
                        onClick={calculatePrices}
                        title="Calculate missing price or margin"
                      >
                        <FaCalculator />
                      </button>
                    </div>

                    {/* Row 3: Installation fields and type selector */}
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">
                          {newPrice.installation_type === 'percentage_on_net' && 'Installation % on Net'}
                          {newPrice.installation_type === 'percentage_on_brut' && 'Installation % on Brut'}
                          {newPrice.installation_type === 'fixed_price' && 'Installation Fixed Price'}
                        </label>
                        {newPrice.installation_type === 'percentage_on_net' && (
                          <input
                            type="number"
                            placeholder="0.00"
                            value={newPrice.installation_percentage_on_net}
                            onChange={(e) => handlePriceChange('installation_percentage_on_net', e.target.value)}
                            className="input_field__input_box--text"
                          />
                        )}
                        {newPrice.installation_type === 'percentage_on_brut' && (
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={newPrice.installation_percentage_on_brut}
                            onChange={(e) => handlePriceChange('installation_percentage_on_brut', e.target.value)}
                            className="input_field__input_box--text"
                          />
                        )}
                        {newPrice.installation_type === 'fixed_price' && (
                          <input
                            type="number"
                            placeholder="0.00"
                            value={newPrice.installation_fixed_price}
                            onChange={(e) => handlePriceChange('installation_fixed_price', e.target.value)}
                            className="input_field__input_box--text"
                          />
                        )}
                      </div>
                    </div>
                    <div className="grid-item grid-column-span-2">
                      <div className="price-input-group">
                        <label className="price-input-label">Installation Type</label>
                        <div className="price-type-selector">
                          <label>
                            <input
                              type="radio"
                              name="installationType"
                              value="percentage_on_net"
                              checked={newPrice.installation_type === 'percentage_on_net'}
                              onChange={() => handlePriceChange('installation_type', 'percentage_on_net')}
                            />
                            % on Net
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="installationType"
                              value="percentage_on_brut"
                              checked={newPrice.installation_type === 'percentage_on_brut'}
                              onChange={() => handlePriceChange('installation_type', 'percentage_on_brut')}
                            />
                            % on Brut
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="installationType"
                              value="fixed_price"
                              checked={newPrice.installation_type === 'fixed_price'}
                              onChange={() => handlePriceChange('installation_type', 'fixed_price')}
                            />
                            Fixed Price
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="grid-item">
                      {/* Empty cell for grid alignment */}
                    </div>

                    {/* Row 4: Net installation, Margin installation, Brut installation, Total price */}
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Net Installation Price</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newPrice.price_net_installation}
                          onChange={(e) => handlePriceChange('price_net_installation', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Margin % Installation</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newPrice.margin_installation}
                          onChange={(e) => handlePriceChange('margin_installation', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Brut Installation Price</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newPrice.price_brut_installation}
                          onChange={(e) => handlePriceChange('price_brut_installation', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      <button
                        className="calculator-button"
                        onClick={calculateInstallationPrices}
                        title="Calculate missing installation price or margin"
                      >
                        <FaCalculator />
                      </button>
                    </div>

                    {/* Row 5: Total price including installation */}
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label"><strong>Total Price</strong></label>
                        <span style={{ fontSize: '0.7rem', color: 'var(--secondairy-medium-gray)' }}>Product + Installation</span>
                      </div>
                    </div>
                    <div className="grid-item">
                      {/* Empty cell */}
                    </div>
                    <div className="grid-item">
                      <div className="price-input-group">
                        <label className="price-input-label">Total Price (Product + Installation)</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={newPrice.price_brut_incl_installation}
                          onChange={(e) => handlePriceChange('price_brut_incl_installation', e.target.value)}
                          className="input_field__input_box--text"
                        />
                      </div>
                    </div>
                    <div className="grid-item">
                      {/* Empty cell for grid alignment */}
                    </div>
                  </div>

                  {/* Add Price button below the grid */}
                  <div className="form-button-container">
                    <button
                      onClick={AddPrice}
                      className="primary-button"
                    >
                      {editingPriceId ? 'Update Price' : 'Add Price'}
                    </button>
                    {editingPriceId && (
                      <button
                        onClick={handleCancelEdit}
                        className="primary-button red"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Display supplier prices */}
              <div className="prices-list">
                <table className="prices-table">
                  <thead>
                    <tr>
                      <th>Record date</th>
                      <th>Installer</th>
                      <th>Supplier</th>
                      <th>SKU</th>
                      <th>Unit</th>
                      <th>Net excl. install</th>
                      <th>Margin excl.</th>
                      <th>Brut excl. install</th>
                      <th>Installation</th>
                      <th>Net install</th>
                      <th>Margin install</th>
                      <th>Brut install</th>
                      <th>Total price</th>
                      <th className="usage-column">Projects</th>
                      <th className="usage-column">Units</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prices.map((price) => (
                      <tr key={price.id} className={editingPriceId === price.id ? 'selected-row' : ''}>
                        <td>{price.record_date ? formatDate(price.record_date) : '-'}</td>
                        <td>{price.installer_name || '-'}</td>
                        <td>{price.supplier_name || '-'}</td>
                        <td>{price.SKU_supplier || '-'}</td>
                        <td>{price.unit || '-'}</td>
                        <td>€{price.price_net_excl_installation}</td>
                        <td>{price.margin_excl_installation}%</td>
                        <td>€{price.price_brut_excl_installation}</td>
                        <td>
                          {price.installation_percentage_on_net && Number(price.installation_percentage_on_net) > 0 ? (
                            <span>{Number(price.installation_percentage_on_net)}% on net</span>
                          ) : price.installation_percentage_on_brut && Number(price.installation_percentage_on_brut) > 0 ? (
                            <span>{Number(price.installation_percentage_on_brut)}% on brut</span>
                          ) : price.installation_fixed_price && Number(price.installation_fixed_price) > 0 ? (
                            <span>€{Number(price.installation_fixed_price).toFixed(2)} fixed</span>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td>€{price.price_net_installation}</td>
                        <td>{price.margin_installation}%</td>
                        <td>€{price.price_brut_installation}</td>
                        <td>€{price.price_brut_incl_installation}</td>
                        <td className="usage-column">{renderUsageLabels(price.purchase_order_projects, `${price.id}-projects`)}</td>
                        <td className="usage-column">{renderUsageLabels(price.purchase_order_units, `${price.id}-units`)}</td>
                        <td>
                          {edit && (
                            <div className="actions-container">
                              <button
                                onClick={() => handleEditPrice(price)}
                                className="edit-button"
                                title="Edit price"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handleDeletePrice(price.id)}
                                className={`delete-button ${!price.updatable ? 'disa' : ''}`}
                                title={!price.updatable ? "Cannot delete - price is linked to purchase order(s)" : "Delete price"}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {edit && !showPriceForm && (
                <div className="form-button-container">
                  <button
                    onClick={handleCreateNewPrice}
                    className="primary-button"
                  >
                    + Create new
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Images section */}
          <div
            className={`form__section ${expandedSection === 'image_upload' ? 'expanded' : ''}`}
            id="image_upload"
          >
            <h3 onClick={() => toggleSectionExpand('image_upload')}>Images</h3>
            
            <div className='section-content'>
              <div className='file__uploader_section'>
                <div className="photo-grid">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="photo-grid-item"
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      {edit && (
                        <button
                          className="delete-image-button"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteFile(image.id)
                          }}
                        >
                          <CloseIcon />
                        </button>
                      )}
                      <img src={image.thumb_url} alt={`Product ${index + 1}`} />
                    </div>
                  ))}
                  {edit && (
                    <label className="photo-grid-item add-photo-button">
                      <input
                        type="file"
                        accept="image/*"
                        multiple={true}
                        onChange={(e) => handleUploadFiles(e, "image")}
                      />
                      +
                    </label>
                  )}
                </div>
                {selectedImageIndex !== null && (
                  <ImageViewer
                    images={images}
                    selectedIndex={selectedImageIndex}
                    onClose={() => setSelectedImageIndex(null)}
                    onNavigate={handleImageViewerNavigate}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Documents section */}
          <div
            className={`form__section ${expandedSection === 'file_upload' ? 'expanded full-width-content' : ''}`}
            id="file_upload"
          >
            <h3 onClick={() => toggleSectionExpand('file_upload')}>Documents</h3>
            
            <div className='section-content'>
              <DocumentsSection scope="product" entityId={productID} editable={edit} />
            </div>
          </div>
        </div>
      </div>

      <PriceEditWarningPopup
        isOpen={showPriceWarning}
        onConfirm={confirmPriceEdit}
        onCancel={() => setShowPriceWarning(false)}
        affectedPOCount={priceEditWarningData.affectedPOCount || 0}
      />

    </div>
  )
}

export default ProductDetailPage
